import type { LocalDate } from "@js-joda/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useScheduleStore } from "web-apps/company/stores/schedule/scheduleStore";
import { isOk } from "utils/result";
import {
  appendScheduleDayShiftsPage,
  createScheduleDayShiftsPageState,
  type ScheduleDayShiftsPageState,
} from "./scheduleDayShiftsPagination.logic";

type ScheduleDayShiftsState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | (ScheduleDayShiftsPageState & {
      readonly isLoadingMore: boolean;
      readonly loadMoreError: boolean;
      readonly status: "ready";
    });

interface UseScheduleDayShiftsParams {
  readonly companyId: number | undefined;
  readonly date: LocalDate | null;
}

interface UseScheduleDayShiftsResult {
  readonly loadMore: () => void;
  readonly state: ScheduleDayShiftsState;
}

const scheduleDayPageSize = 50;

export const useScheduleDayShifts = ({
  companyId,
  date,
}: UseScheduleDayShiftsParams): UseScheduleDayShiftsResult => {
  const [, scheduleActions] = useScheduleStore();
  const [state, setState] = useState<ScheduleDayShiftsState>({
    status: "idle",
  });
  const isLoadingMoreRef = useRef(false);
  const requestVersionRef = useRef(0);

  useEffect(() => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    isLoadingMoreRef.current = false;
    if (!date || !companyId) {
      setState({ status: "idle" });
      return;
    }
    let isActive = true;
    setState({ status: "loading" });
    const load = async (): Promise<void> => {
      const result = await scheduleActions.getScheduleDay({
        companyId,
        date,
        page: 0,
        pageSize: scheduleDayPageSize,
      });
      if (!isActive || requestVersion !== requestVersionRef.current) return;
      setState(
        isOk(result)
          ? {
              ...createScheduleDayShiftsPageState({
                data: result.value,
                page: 0,
              }),
              isLoadingMore: false,
              loadMoreError: false,
              status: "ready",
            }
          : { status: "error" },
      );
    };
    void load();
    return () => {
      isActive = false;
    };
  }, [companyId, date, scheduleActions]);

  const loadMore = useCallback((): void => {
    if (!companyId || !date || state.status !== "ready") return;
    if (!state.hasMore || isLoadingMoreRef.current) return;

    const requestVersion = requestVersionRef.current;
    isLoadingMoreRef.current = true;
    setState({ ...state, isLoadingMore: true, loadMoreError: false });

    const load = async (): Promise<void> => {
      const result = await scheduleActions.getScheduleDay({
        companyId,
        date,
        page: state.page + 1,
        pageSize: scheduleDayPageSize,
      });
      if (requestVersion !== requestVersionRef.current) return;
      isLoadingMoreRef.current = false;
      if (!isOk(result)) {
        setState({ ...state, isLoadingMore: false, loadMoreError: true });
        return;
      }

      setState({
        ...appendScheduleDayShiftsPage({ current: state, next: result.value }),
        isLoadingMore: false,
        loadMoreError: false,
        status: "ready",
      });
    };
    void load();
  }, [companyId, date, scheduleActions, state]);

  return { loadMore, state };
};
